import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { PRODUCT_OPTION_LABELS, PRODUCT_OPTION_PRESETS, WEIGHT_LABELS } from 'constants/productDropdownOptions';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { buildProductImageUrl, extractProductImages } from './productImageUtils';

const UpdateProduct = () => {
  const BACKEND = process.env.REACT_APP_BACKEND;
  const OTHER_VALUE = '__other__';
  const selectFields = ['weight', 'rating'];
  const NUMERIC_FIELDS = new Set(['selling_price', 'shipping_charge', 'original_price', 'stock_quantity', 'weight', 'rating']);

  const { id } = useParams();
  const navigate = useNavigate();

  const [optionSelections, setOptionSelections] = useState({});
  const [customOptionValues, setCustomOptionValues] = useState({});
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    keywords: '',
    selling_price: '',
    shipping_charge: '',
    original_price: '',
    description: '',
    benefit_description: '',
    category_id: '',
    main_category_ids: [],
    sub_category_id: '',
    stock_quantity: '',
    weight: '',
    status: 'active',
    rating: ''
  });
  const [firstImage, setFirstImage] = useState(null);
  const [moreImages, setMoreImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, mainCatRes, subCatRes] = await Promise.all([
          getRequest('categories'),
          getRequest('main-categories'),
          getRequest('sub-categories'),
        ]);
        setCategories(catRes?.data || []);
        setMainCategories(mainCatRes?.data || []);
        setSubCategories(subCatRes?.data || []);
      } catch (err) {
        console.error('Failed to fetch category data:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      setFilteredSubCategories(subCategories.filter((s) => Number(s.category_id) === Number(formData.category_id)));
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.category_id, subCategories]);

  const formatOptionLabel = (fieldName, value) => {
    if (fieldName === 'weight') return WEIGHT_LABELS[value] || `${value}kg`;
    return value;
  };

  const normalizeNumberValue = (fieldName, rawValue) => {
    const str = String(rawValue ?? '').trim();
    if (!str) return '';
    const cleaned = fieldName === 'weight' ? str.replace(/[^0-9.]/g, '') : str;
    const n = Number.parseFloat(cleaned);
    if (Number.isNaN(n)) return '';
    return String(n);
  };

  const parseMainCategoryIdsValue = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(Number);
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.map(Number);
    } catch (e) {
      // ignore parse errors
    }
    if (typeof raw === 'string' && raw.includes(',')) {
      return raw
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n));
    }
    return [];
  };

  const hydrateOptionSelections = (productPayload) => {
    const nextSelections = {};
    const nextCustom = {};

    for (const field of selectFields) {
      const value = productPayload?.[field] ?? '';
      const valueString = value === '' ? '' : String(value);
      const presets = PRODUCT_OPTION_PRESETS[field] || [];

      if (!valueString) {
        nextSelections[field] = '';
        nextCustom[field] = '';
      } else if (presets.includes(valueString)) {
        nextSelections[field] = valueString;
        nextCustom[field] = '';
      } else {
        const normalizedForNumberFields = NUMERIC_FIELDS.has(field) ? normalizeNumberValue(field, valueString) : valueString;
        const matchesPreset = normalizedForNumberFields && presets.includes(normalizedForNumberFields);

        if (matchesPreset) {
          nextSelections[field] = normalizedForNumberFields;
          nextCustom[field] = '';
          continue;
        }

        nextSelections[field] = OTHER_VALUE;
        nextCustom[field] = normalizedForNumberFields || valueString;
      }
    }

    setOptionSelections(nextSelections);
    setCustomOptionValues(nextCustom);
  };

  useEffect(() => {
    let mounted = true;

    const fetchProduct = async () => {
      setLoadingProduct(true);
      try {
        let res;
        try {
          res = await getRequest(`products/one/${id}`);
        } catch {
          res = await getRequest(`products/${id}`);
        }
        const payload = res?.data?.data ?? res?.data?.product ?? res?.data ?? {};

        if (!mounted) return;

        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
          toast.error('Product details not found.');
          return;
        }

        setFormData((prev) => ({
          ...prev,
          ...payload,
          keywords: payload.keywords || '',
          main_category_ids: parseMainCategoryIdsValue(payload.main_category_ids),
          status: payload.status || 'active'
        }));
        hydrateOptionSelections(payload);
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to load product details';
        toast.error(msg);
        console.error('Failed to fetch product details:', error?.response?.data || error);
      } finally {
        if (mounted) {
          setLoadingProduct(false);
        }
      }
    };

    fetchProduct();
    return () => {
      mounted = false;
    };
  }, [id]);

  const setFieldValue = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleMainCategoryToggle = (id, checked) => {
    setFormData((prev) => {
      const numericId = Number(id);
      const exists = prev.main_category_ids.includes(numericId);
      if (checked && !exists) {
        return { ...prev, main_category_ids: [...prev.main_category_ids, numericId] };
      }
      if (!checked && exists) {
        return { ...prev, main_category_ids: prev.main_category_ids.filter((x) => x !== numericId) };
      }
      return prev;
    });
  };

  const handleOptionSelectChange = (fieldName, selectedValue) => {
    setOptionSelections((prev) => ({ ...prev, [fieldName]: selectedValue }));

    if (!selectedValue) {
      setCustomOptionValues((prev) => ({ ...prev, [fieldName]: '' }));
      setFieldValue(fieldName, '');
      return;
    }

    if (selectedValue === OTHER_VALUE) {
      const existingCustom = customOptionValues[fieldName] || '';
      setFieldValue(fieldName, existingCustom);
      return;
    }

    setCustomOptionValues((prev) => ({ ...prev, [fieldName]: '' }));
    setFieldValue(fieldName, selectedValue);
  };

  const handleCustomOptionChange = (fieldName, value) => {
    setCustomOptionValues((prev) => ({ ...prev, [fieldName]: value }));
    setFieldValue(fieldName, value);
  };

  const renderSelectWithOther = (fieldName) => {
    const options = PRODUCT_OPTION_PRESETS[fieldName] || [];
    const label = PRODUCT_OPTION_LABELS[fieldName] || fieldName;
    const selectedValue = optionSelections[fieldName] ?? '';
    const customValue = customOptionValues[fieldName] ?? '';

    return (
      <>
        <select
          id={fieldName}
          name={fieldName}
          value={selectedValue}
          className="form-control"
          onChange={(e) => handleOptionSelectChange(fieldName, e.target.value)}
        >
          <option value="">{`Select ${label}`}</option>
          {options.map((value) => (
            <option key={value} value={value}>
              {formatOptionLabel(fieldName, value)}
            </option>
          ))}
          <option value={OTHER_VALUE}>Other (Type)</option>
        </select>

        {selectedValue === OTHER_VALUE && (
          <input
            type="text"
            className="form-control mt-2"
            placeholder={`Enter ${label}`}
            value={customValue}
            onChange={(e) => handleCustomOptionChange(fieldName, e.target.value)}
          />
        )}
      </>
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFirstImageChange = (e) => {
    setFirstImage(e.target.files[0] || null);
  };

  const handleMoreImagesChange = (e) => {
    setMoreImages(Array.from(e.target.files || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      const formDataToSend = new FormData();
      const appendIf = (key, value) => { if (value !== '' && value != null) formDataToSend.append(key, value); };

      appendIf('name', formData.name || '');
      appendIf('keywords', formData.keywords || '');
      appendIf('selling_price', normalizeNumberValue('selling_price', formData.selling_price));
      appendIf('shipping_charge', normalizeNumberValue('shipping_charge', formData.shipping_charge));
      appendIf('original_price', normalizeNumberValue('original_price', formData.original_price));
      appendIf('description', formData.description || '');
      appendIf('benefit_description', formData.benefit_description || '');
      appendIf('category_id', formData.category_id);
      if (formData.main_category_ids && formData.main_category_ids.length) {
        formDataToSend.append('main_category_ids', JSON.stringify(formData.main_category_ids));
      }
      appendIf('sub_category_id', formData.sub_category_id);
      appendIf('stock_quantity', normalizeNumberValue('stock_quantity', formData.stock_quantity));
      appendIf('weight', normalizeNumberValue('weight', formData.weight));
      appendIf('status', formData.status);
      appendIf('rating', normalizeNumberValue('rating', formData.rating));

      if (firstImage) {
        formDataToSend.append('images', firstImage);
      }
      for (let i = 0; i < moreImages.length; i++) {
        formDataToSend.append('images', moreImages[i]);
      }

      await patchRequest(`products/${id}`, formDataToSend);
      toast.success('Product updated successfully!');
      navigate('/products');
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'There was a problem updating the product';
      toast.error(msg);
      console.error('There was a problem updating the product:', error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid px-lg-5">
      <div className="mainheadig">
        <h4 className="text-white font-weight-bold">Update Product</h4>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="row p-3">
          <div className="col-sm-4 mt-2">
            <label htmlFor="productName">Product Name</label>
            <input
              type="text"
              id="productName"
              name="name"
              value={formData.name || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="keywords">Keywords</label>
            <input
              type="text"
              id="keywords"
              name="keywords"
              placeholder="Enter keywords (comma separated)"
              value={formData.keywords || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="selling_price">Selling Price</label>
            <input
              type="number"
              step="1"
              id="selling_price"
              name="selling_price"
              value={formData.selling_price || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="shipping_charge">Shipping Charge</label>
            <input
              type="number"
              step="0.01"
              id="shipping_charge"
              name="shipping_charge"
              value={formData.shipping_charge || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="original_price">Original Price</label>
            <input
              type="number"
              step="1"
              id="original_price"
              name="original_price"
              value={formData.original_price || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="stock_quantity">Stock Quantity</label>
            <input
              type="number"
              step="1"
              id="stock_quantity"
              name="stock_quantity"
              value={formData.stock_quantity || ''}
              className="form-control"
              onChange={handleChange}
            />
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="weight">Weight (kg)</label>
            {renderSelectWithOther('weight')}
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="rating">Rating</label>
            {renderSelectWithOther('rating')}
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status || 'active'}
              className="form-control"
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="category_id">Category</label>
            <select id="category_id" name="category_id" value={formData.category_id || ''} className="form-control" onChange={handleChange}>
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-4 mt-2">
            <div className="admin-label">Main Categories</div>
            <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
              {mainCategories.length === 0 && <div className="text-muted small">No main categories found</div>}
              {mainCategories.map((cat) => (
                <div className="form-check" key={cat.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`main_cat_${cat.id}`}
                    value={cat.id}
                    checked={(formData.main_category_ids || []).includes(Number(cat.id))}
                    onChange={(e) => handleMainCategoryToggle(cat.id, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`main_cat_${cat.id}`}>{cat.name}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="col-sm-4 mt-2">
            <label htmlFor="sub_category_id">Sub Category</label>
            <select id="sub_category_id" name="sub_category_id" value={formData.sub_category_id || ''} className="form-control" onChange={handleChange}>
              <option value="">Select Sub Category</option>
              {filteredSubCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-12 mt-2">
            <label htmlFor="productDescription">Description</label>
            <textarea
              rows={5}
              id="productDescription"
              name="description"
              value={formData.description || ''}
              className="form-control"
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="col-sm-12 mt-2">
            <label htmlFor="benefit_description">Benefit Description</label>
            <textarea
              rows={3}
              id="benefit_description"
              name="benefit_description"
              value={formData.benefit_description || ''}
              className="form-control"
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="col-sm-3 mt-2">
            <label htmlFor="UpdateFirstImage">Choose First Image</label>
            <input
              type="file"
              id="UpdateFirstImage"
              accept="image/*"
              className="form-control"
              onChange={handleFirstImageChange}
            />
            {firstImage && (
              <div className="mt-2">
                <img
                  src={URL.createObjectURL(firstImage)}
                  alt="First"
                  className="border"
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                />
                <span className="ml-2 text-success font-weight-bold">First Image</span>
              </div>
            )}
          </div>
          <div className="col-sm-3 mt-2">
            <label htmlFor="UpdateMoreImages">Choose More Images</label>
            <input
              type="file"
              id="UpdateMoreImages"
              accept="image/*"
              multiple
              className="form-control"
              onChange={handleMoreImagesChange}
            />
            {moreImages.length > 0 && (
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }} className="mt-2">
                {moreImages.map((file, i) => (
                  <img
                    key={i}
                    src={URL.createObjectURL(file)}
                    className="border"
                    alt={`New Product ${i}`}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="col-sm-6 mt-2">
            <div className="font-weight-bold mb-1">Current Images</div>
            {(() => {
              const imgs = extractProductImages(formData);
              if (imgs.length === 0) return <p className="text-muted">No images</p>;

              return (
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {imgs.map((img, i) => (
                    <div key={i} className="text-center">
                      <img
                        src={buildProductImageUrl(img, BACKEND)}
                        className="border"
                        alt={`Current Product ${i}`}
                        style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                      />
                      {i === 0 && <div className="text-success font-weight-bold" style={{ fontSize: 11 }}>First</div>}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
        <div className="my-3 text-right">
          <button type="submit" className="btn addbtn" disabled={loading || loadingProduct}>
            {loading ? 'Updating...' : loadingProduct ? 'Loading...' : 'Update Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateProduct;
